import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useCompanySettings } from '../../context/CompanySettingsContext';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import useLogoReset from '../../hooks/useLogoReset';
import useClearSelectionOnFilterChange from '../../hooks/useClearSelectionOnFilterChange';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { mergeColumnsWithDefault } from '../../utils/mergePrefsWithDefaults';
import { format } from 'date-fns';
import {
  Search, Plus, Truck, MapPin, FileSignature, PaintBucket,
  MoreHorizontal, RefreshCw, Loader2, Settings2,
  Copy, CheckSquare, X, Filter, Upload, ListOrdered, Check, CalendarIcon, Wifi, WifiOff
} from 'lucide-react';
import { AddLoadSheet } from './AddLoadSheet';
import { DispatchModal } from './DispatchModal';
import { EditLoadSheet } from './EditLoadSheet';
import { ColumnSettingsModal } from './ColumnSettingsModal';
import { StatusOrderModal } from './StatusOrderModal';
import { LocationChangeModal } from './LocationChangeModal';
import { StatusColorSettings } from './StatusColorSettings';
import { CellColorPicker } from '@/components/ui/CellColorPicker';
import { CellDateColorPicker } from '@/components/ui/CellDateColorPicker';
import { CellSelectColorPicker } from '@/components/ui/CellSelectColorPicker';
import { ColoredText } from '@/components/ui/ColoredText';
import { BulkDOUpload } from './BulkDOUpload';
import CompanySetupModal from './CompanySetupModal';
import { useBroadcastSync, SYNC_TYPES } from '../../hooks/useBroadcastSync';
import { useBulkSelection, useColumnManagement, useInlineEdit, useDateFormat, useInAppSearchShortcut } from '../../hooks';
import { useShipmentBulkActions } from '../../hooks/useShipmentBulkActions';
import { useSavedSearches } from '../../hooks/useSavedSearches';
import { useShipmentWebSocket } from '../../hooks/useShipmentWebSocket';
import { useWebSocketSubscribe } from '../../context/WebSocketContext';
import { ContainerCommentsDialog } from './components/ContainerCommentsDialog';
import { ContainerTimelineDialog } from './components/ContainerTimelineDialog';
import { TrackingHealthBanner } from './components/TrackingHealthBanner';
import { Pagination, ShipmentFilterBar, BulkSelectionBar, StatusColorLegend, BulkUpdateModal, DeleteConfirmationModal, ShipmentTableCell, DataTable } from './components';
import {
  PROTECTED_COLUMNS,
  STATUS_COLORS,
  DEFAULT_STATUS_ROW_COLORS,
  STATUS_OPTIONS,
  DEFAULT_COLUMNS,
  DEFAULT_STATUS_ORDER,
  COLOR_PICKABLE_COLUMNS,
  DATE_FIELDS,
} from './shipmentDashboardConstants';
import {
  getRowBackgroundColor as resolveRowBackgroundColor,
  getTextColorForStatus as resolveTextColorForStatus,
  getCellTextColor as resolveCellTextColor,
  getMinColumnWidth as resolveMinColumnWidth,
  getFlexColumnWidth as resolveFlexColumnWidth,
  getDaysUntilLFD,
  shouldLFDBlink,
  formatTimeWindow,
  sortShipments,
  mergeStatusOrderWithDefault,
  buildColumnLabelResolver,
} from './shipmentDashboardHelpers';

export const ShipmentDashboard = () => {
  const { api, user } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { formatDate: formatDateWithSettings, dateFormat } = useDateFormat();
  // Hijack Ctrl/Cmd+F → in-app search (server-backed, full dataset).
  useInAppSearchShortcut('[data-testid="shipment-search-input"]');
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [hiddenStatuses, setHiddenStatuses] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalShipments, setTotalShipments] = useState(0);
  const [pageSize] = useState(10000); // Show all loads on single page
  
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  
  // Delete load state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteShipment, setDeleteShipment] = useState(null);
  const [deletePin, setDeletePin] = useState('');
  const [deletingLoad, setDeletingLoad] = useState(false);
  
  // Stats and UI state
  const [stats, setStats] = useState(null);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [lastEditedShipmentId, setLastEditedShipmentId] = useState(null);
  const tableRef = useRef(null);
  
  // Location change modal state
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locationShipment, setLocationShipment] = useState(null);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [sslFilter, setSslFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Bulk selection using shared hook
  const {
    selectedIds: selectedShipments,
    selectedCount,
    toggleSelection: toggleSelectShipment,
    toggleSelectAll,
    isSelected,
    isAllSelected,
    isSomeSelected,
    deselectAll
  } = useBulkSelection({
    items: shipments,
    idField: 'id'
  });
  
  // Inline edit using shared hook
  const {
    editCell: inlineEditCell,
    editValue: inlineEditValue,
    setEditValue: setInlineEditValue,
    saving: savingInlineEdit,
    inputRef: inlineEditInputRef,
    startEdit: startInlineEdit,
    cancelEdit: cancelInlineEdit,
    saveEdit: handleInlineEditSave,
    handleKeyDown: handleInlineEditKeyDown,
    isEditing: isInlineEditing
  } = useInlineEdit({
    onSave: async (shipmentId, field, value) => {
      await api.put(`/shipments/${shipmentId}`, { [field]: value || null });
      setShipments(prev => prev.map(s => 
        s.id === shipmentId ? { ...s, [field]: value } : s
      ));
      setLastEditedShipmentId(shipmentId);
      // Broadcast the update
      broadcast(SYNC_TYPES.SHIPMENT_UPDATED, {
        shipmentId,
        updates: { [field]: value },
      });
      setTimeout(() => setLastEditedShipmentId(null), 3000);
      toast.success('Updated successfully');
    }
  });

  // Column management using shared hook with custom API pattern
  const {
    columns,
    setColumns,
    visibleColumns,
    columnsChanged,
    savingColumns,
    draggedColumn,
    dragOverColumn,
    resizingColumn,
    loadColumnPreferences,
    saveColumnPreferences,
    toggleColumnVisibility,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleResizeStart,
    resetToDefaults
  } = useColumnManagement({
    defaultColumns: DEFAULT_COLUMNS,
    api,
    protectedColumns: PROTECTED_COLUMNS,
    customLoadFn: async () => {
      try {
        const response = await api.get('/user/preferences/columns?dashboard=imports');
        if (response.data?.columns && response.data.columns.length > 0) {
          const savedColumns = response.data.columns;
          // Merge saved columns with defaults, maintaining order from saved preferences
          const mergedColumns = DEFAULT_COLUMNS.map(defaultCol => {
            const savedCol = savedColumns.find(c => c.id === defaultCol.id);
            if (savedCol) {
              return { ...defaultCol, ...savedCol, width: savedCol.width || defaultCol.width };
            }
            return defaultCol;
          });
          
          // Maintain the order from saved preferences
          const orderedColumns = [];
          savedColumns.forEach(savedCol => {
            const merged = mergedColumns.find(c => c.id === savedCol.id);
            if (merged) orderedColumns.push(merged);
          });
          
          // Add any new columns that weren't in saved preferences
          mergedColumns.forEach(col => {
            if (!orderedColumns.find(c => c.id === col.id)) {
              if (col.id === 'marker') {
                orderedColumns.unshift(col);
              } else {
                orderedColumns.push(col);
              }
            }
          });
          
          return orderedColumns;
        }
        return null;
      } catch (error) {
        console.error('Failed to load column preferences:', error);
        return null;
      }
    },
    customSaveFn: async (columnsToSave) => {
      await api.post('/user/preferences/columns', { 
        columns: columnsToSave,
        dashboard: 'imports'
      });
      toast.success('Column layout saved');
    }
  });
  
  // Bulk action modal states — modals are owned by the dashboard (UI state),
  // network/side-effects live in `useShipmentBulkActions` below.
  const [bulkStatusModalOpen, setBulkStatusModalOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');

  // Bulk LFD and Yard state
  const [bulkLFDModalOpen, setBulkLFDModalOpen] = useState(false);
  const [bulkLFD, setBulkLFD] = useState(null);
  const [bulkYardModalOpen, setBulkYardModalOpen] = useState(false);
  const [bulkTerminalModalOpen, setBulkTerminalModalOpen] = useState(false);
  const [bulkTerminal, setBulkTerminal] = useState('');
  const [bulkYard, setBulkYard] = useState('');
  
  // Status color customization (admin only)
  const [statusColors, setStatusColors] = useState(DEFAULT_STATUS_ROW_COLORS);
  const [colorSettingsOpen, setColorSettingsOpen] = useState(false);
  
  // Status sort order (customizable per user)
  const [statusOrder, setStatusOrder] = useState(DEFAULT_STATUS_ORDER);
  const [statusOrderModalOpen, setStatusOrderModalOpen] = useState(false);
  
  // iter 265.3 — Row Selection Persistence Toggle. When true, changing any
  // dashboard filter deselects bulk-selected rows. Persisted per-user.
  const [filterClearSelection, setFilterClearSelection] = useState(false);
  
  // Bulk DO Upload
  const [bulkDOUploadOpen, setBulkDOUploadOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Handle bulkDO query parameter from redirect
  useEffect(() => {
    if (searchParams.get('bulkDO') === 'true') {
      setBulkDOUploadOpen(true);
      // Clear the query parameter
      searchParams.delete('bulkDO');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle newShipment query parameter from Customers page Quick Action
  useEffect(() => {
    if (searchParams.get('newShipment') === 'true') {
      const customerId = searchParams.get('customerId');
      const customerName = searchParams.get('customerName');
      
      // Open new load dialog with pre-selected customer
      setAddSheetOpen(true);
      
      // If we have customer info, we could pre-fill it (if the dialog supports it)
      // For now, just open the dialog - the user will select the customer
      if (customerName) {
        // Toast to remind user which customer they selected
        console.log(`Creating shipment for customer: ${customerName} (${customerId})`);
      }
      
      // Clear the query parameters
      searchParams.delete('newShipment');
      searchParams.delete('customerId');
      searchParams.delete('customerName');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  
  // Company setup modal (admin only, first-time)
  const [companySetupOpen, setCompanySetupOpen] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  
  // Ref to hold fetchShipments for use in sync handler
  const fetchShipmentsRef = useRef(null);
  // Mirror of the latest shipments array — used by fetchShipments to merge
  // fresh server rows with cached fields (e.g. comment_count) without
  // adding `shipments` to the fetch callback's dep array (would cause loops).
  const shipmentsRef = useRef([]);
  // Monotonic counter that bumps after every successful fetchShipments —
  // drives the bulk-counts useEffect so it re-runs even when the ID set
  // is unchanged. Fixes the "comment dot only appears after 3 refreshes" bug.
  const [commentCountsTick, setCommentCountsTick] = useState(0);
  
  // Handle sync messages from other tabs
  const handleSyncMessage = useCallback((message) => {
    const { type, payload } = message;
    
    switch (type) {
      case SYNC_TYPES.SHIPMENT_UPDATED:
        // Update the specific shipment in local state
        setShipments(prev => prev.map(s => 
          s.id === payload.shipmentId 
            ? { ...s, ...payload.updates }
            : s
        ));
        break;
        
      case SYNC_TYPES.CELL_COLOR_UPDATED:
        // Update cell color
        setShipments(prev => prev.map(s => 
          s.id === payload.shipmentId 
            ? { 
                ...s, 
                cell_colors: { ...(s.cell_colors || {}), [payload.field]: payload.color }
              }
            : s
        ));
        break;
        
      case SYNC_TYPES.STATUS_UPDATED:
        // Update shipment status
        setShipments(prev => prev.map(s => 
          s.id === payload.shipmentId 
            ? { ...s, status: payload.status }
            : s
        ));
        break;
        
      case SYNC_TYPES.SHIPMENT_DELETED:
        // Remove deleted shipment from local state
        setShipments(prev => prev.filter(s => s.id !== payload.shipmentId));
        break;
        
      case SYNC_TYPES.SHIPMENT_CREATED:
      case SYNC_TYPES.REFRESH_ALL:
        // For create/refresh, reload all data
        if (fetchShipmentsRef.current) {
          fetchShipmentsRef.current();
        }
        break;
        
      default:
        break;
    }
  }, []);
  
  // Initialize broadcast sync
  const { broadcast } = useBroadcastSync(handleSyncMessage);
  
  // WebSocket for real-time shipment updates from server
  const { connected: wsConnected } = useShipmentWebSocket({
    onShipmentCreated: (shipment) => {
      // Add the new shipment to the top of the list
      setShipments(prev => [shipment, ...prev.filter(s => s.id !== shipment.id)]);
      setTotalShipments(prev => prev + 1);
    },
    onShipmentUpdated: (shipment, changes) => {
      // Update the shipment in local state
      setShipments(prev => prev.map(s => 
        s.id === shipment.id ? { ...s, ...shipment } : s
      ));
    },
    onShipmentDeleted: (shipmentId) => {
      // Remove deleted shipment
      setShipments(prev => prev.filter(s => s.id !== shipmentId));
      setTotalShipments(prev => Math.max(0, prev - 1));
    },
    onStatusChanged: (shipment, oldStatus, newStatus) => {
      // Update status in local state
      setShipments(prev => prev.map(s => 
        s.id === shipment.id ? { ...s, status: newStatus, ...shipment } : s
      ));
    },
    onBulkUpdated: () => {
      // For bulk updates, refresh the entire list
      if (fetchShipmentsRef.current) {
        fetchShipmentsRef.current();
      }
    },
    showToasts: true,
    playSound: false // Sound is managed by user preferences
  });

  // ----- Container Comments (right-click / long-press on container_number) -----
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [commentsShipment, setCommentsShipment] = useState(null);

  const handleCommentsClick = useCallback((shipment) => {
    setCommentsShipment(shipment);
    setCommentsDialogOpen(true);
  }, []);

  // ----- Container Timeline (right-click → Timeline option) -----
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [timelineShipment, setTimelineShipment] = useState(null);

  const handleTimelineClick = useCallback((shipment) => {
    setTimelineShipment(shipment);
    setTimelineDialogOpen(true);
  }, []);

  // Patch the in-memory shipment's comment_count whenever a comment event arrives
  // (from this user or any teammate) so the indicator dot stays in sync.
  const bumpCommentCount = useCallback((shipmentId, delta) => {
    setShipments((prev) => prev.map((s) =>
      s.id === shipmentId
        ? { ...s, comment_count: Math.max(0, (s.comment_count || 0) + delta) }
        : s
    ));
  }, []);

  // Subscribe to comment events on the same WS connection. Drop-forward into a
  // window event so the open dialog (decoupled component) can react too.
  useWebSocketSubscribe((data) => {
    if (!data || !data.type || !data.shipment_id) return;
    if (data.type === 'comment_added') {
      bumpCommentCount(data.shipment_id, +1);
    } else if (data.type === 'comment_deleted') {
      bumpCommentCount(data.shipment_id, -1);
    }
    if (['comment_added', 'comment_updated', 'comment_deleted'].includes(data.type)) {
      window.dispatchEvent(new CustomEvent('shipment-comment-event', { detail: data }));
    }
  });

  const [masterData, setMasterData] = useState({
    ssls: [],
    terminals: [],
    drivers: [],
    container_types: [],
    chassis_types: [],
    locations: [],
    yards: [],
    customs_statuses: []
  });

  // OP (admin) gets full page, Dispatcher gets side panel
  const isOP = user?.role === 'admin' || user?.role === 'op' || user?.role === 'super_admin';
  // Customer has view-only access
  const isCustomer = user?.role === 'customer';
  // Can user edit/dispatch shipments?
  const canEdit = !isCustomer && ['admin', 'dispatcher', 'op', 'super_admin'].includes(user?.role);

  // Thin wrappers over the extracted helpers — bind the current theme + custom
  // status-colour overrides so the closures used by render code keep their old
  // signatures (statusOnly → returns a CSS value).
  const getRowBackgroundColor = (status) => resolveRowBackgroundColor(status, statusColors, isDark);
  const getTextColorForStatus = (status) => resolveTextColorForStatus(status, statusColors, isDark);
  const getCellTextColor = (customColor) => resolveCellTextColor(customColor, isDark);

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchShipments = useCallback(async (page = currentPage) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (includeClosed) params.append('include_closed', 'true');
      params.append('page', page.toString());
      params.append('limit', pageSize.toString());
      
      // Apply server-side filters
      if (statusFilter) params.append('status', statusFilter);
      if (sslFilter) params.append('ssl_id', sslFilter);
      if (driverFilter) params.append('driver_id', driverFilter);
      if (customerFilter) params.append('customer_id', customerFilter);
      if (hiddenStatuses.length > 0) params.append('hidden_statuses', hiddenStatuses.join(','));
      
      const response = await api.get(`/shipments?${params.toString()}`);
      
      // Helper: merge fresh server rows with the existing in-memory rows
      // so cached fields the server doesn't return (notably `comment_count`)
      // survive the refetch. Without this the indicator dot flashes off
      // between fetchShipments() resolving and the bulk-counts useEffect
      // re-running.
      const preserveCachedFields = (fresh) => {
        const cache = new Map((shipmentsRef.current || []).map((s) => [s.id, s]));
        return fresh.map((s) => {
          const prev = cache.get(s.id);
          if (!prev) return s;
          return {
            ...s,
            comment_count: s.comment_count ?? prev.comment_count,
          };
        });
      };

      // Handle both paginated and legacy response formats
      if (response.data.shipments) {
        setShipments(preserveCachedFields(response.data.shipments));
        setTotalShipments(response.data.total || 0);
        setTotalPages(response.data.pages || 1);
        setCurrentPage(response.data.page || 1);
      } else if (Array.isArray(response.data)) {
        // Legacy format fallback
        setShipments(preserveCachedFields(response.data));
        setTotalShipments(response.data.length);
        setTotalPages(1);
      }
      // Bump the comment-counts refresh tick — the useEffect below keys on
      // this so the bulk-counts endpoint always re-runs after a refetch,
      // even when the ID set is unchanged (the typical case).
      setCommentCountsTick((t) => t + 1);
    } catch (error) {
      toast.error('Failed to fetch shipments');
    } finally {
      setLoading(false);
    }
  }, [api, debouncedSearchTerm, includeClosed, hiddenStatuses, pageSize, statusFilter, sslFilter, driverFilter, customerFilter, currentPage]);
  
  // Update ref for sync handler
  useEffect(() => {
    fetchShipmentsRef.current = fetchShipments;
  }, [fetchShipments]);

  // Mirror shipments into a ref so fetchShipments can read the latest list
  // for cached-field preservation without re-binding the callback.
  useEffect(() => { shipmentsRef.current = shipments; }, [shipments]);

  // After shipments load, fetch comment counts for all of them in one bulk call,
  // then merge `comment_count` into each row so the indicator dot renders.
  // Keyed on (idSet, tick) so we re-run on every refetch — not just when the
  // set of IDs changes — which restores the dot after refreshes/filter changes.
  const shipmentIdsKey = shipments.map((s) => s.id).sort().join(',');
  useEffect(() => {
    if (!shipmentIdsKey) return;
    let cancelled = false;
    const ids = shipmentIdsKey.split(',').filter(Boolean);
    api.post('/shipments/comments/counts', { shipment_ids: ids })
      .then(({ data }) => {
        if (cancelled || !data) return;
        setShipments((prev) => prev.map((s) => ({ ...s, comment_count: data[s.id] || 0 })));
      })
      .catch(() => { /* counts are non-critical — keep going silently */ });
    return () => { cancelled = true; };
  }, [shipmentIdsKey, commentCountsTick]);

  // Refetch shipments when debounced search term or filters change
  useEffect(() => {
    // Reset to page 1 when search/filters change
    setCurrentPage(1);
    fetchShipments(1);
  }, [debouncedSearchTerm, statusFilter, sslFilter, driverFilter, customerFilter, includeClosed, hiddenStatuses]);
  
  // Fetch when page changes
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchShipments(newPage);
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  }, [api]);

  const fetchMasterData = useCallback(async () => {
    try {
      // Fetch each endpoint separately to prevent one failure from blocking all data
      const fetchSafe = async (endpoint, defaultValue = []) => {
        try {
          const response = await api.get(endpoint);
          return response.data || defaultValue;
        } catch (e) {
          console.warn(`Failed to fetch ${endpoint}:`, e.message);
          return defaultValue;
        }
      };

      const [ssls, terminals, masterDrivers, container_types, chassis_types, locations, yards, customs_statuses, driverUsers] = await Promise.all([
        fetchSafe('/master/ssls'),
        fetchSafe('/master/terminals'),
        fetchSafe('/master/drivers'),
        fetchSafe('/master/container_types'),
        fetchSafe('/master/chassis_types'),
        fetchSafe('/master/locations'),
        fetchSafe('/master/yards'),
        fetchSafe('/master/customs-statuses'),
        fetchSafe('/messages/drivers') // Fetch actual driver users
      ]);
      
      // Combine master drivers with actual driver users
      // Format driver users to match expected structure
      const formattedDriverUsers = (driverUsers || []).map(d => ({
        id: d.user_id,
        name: d.name || d.email,
        code: d.code || '',
        phone: d.phone || ''
      }));
      
      // Merge: prioritize actual driver users, then add any from master that aren't in users
      const masterDriverIds = new Set((masterDrivers || []).map(d => d.id));
      const userDriverIds = new Set(formattedDriverUsers.map(d => d.id));
      const uniqueMasterDrivers = (masterDrivers || []).filter(d => !userDriverIds.has(d.id));
      const allDrivers = [...formattedDriverUsers, ...uniqueMasterDrivers];
      
      setMasterData({
        ssls,
        terminals,
        drivers: allDrivers,
        container_types,
        chassis_types,
        locations,
        yards,
        customs_statuses
      });
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  }, [api]);

  // Load user preferences including custom status colors and sort order
  const fetchUserPreferences = useCallback(async () => {
    try {
      const response = await api.get('/user/preferences');
      if (response.data?.status_colors) {
        setStatusColors({ ...DEFAULT_STATUS_ROW_COLORS, ...response.data.status_colors });
      }
      if (response.data?.status_order && response.data.status_order.length > 0) {
        // iter 264d.16 — always merge with DEFAULT so saved orders missing
        // newer statuses (like "On the way to Return") still slot in
        // sensibly instead of falling to position 999.
        setStatusOrder(mergeStatusOrderWithDefault(response.data.status_order, DEFAULT_STATUS_ORDER));
      }
      if (typeof response.data?.filter_clear_selection === 'boolean') {
        setFilterClearSelection(response.data.filter_clear_selection);
      }
    } catch (error) {
      console.error('Failed to fetch user preferences');
    }
  }, [api]);

  // iter 265.3 — Persist Sticky/Auto-clear toggle from the bulk selection bar.
  const toggleFilterClearSelection = useCallback(async () => {
    const next = !filterClearSelection;
    setFilterClearSelection(next);
    try {
      await api.put('/user/preferences', { filter_clear_selection: next });
    } catch (error) {
      // Roll back on failure so UI reflects true persisted state.
      setFilterClearSelection(!next);
      toast.error('Could not save selection preference');
    }
  }, [api, filterClearSelection]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  }, [api]);

  useEffect(() => {
    // Combined init endpoint for faster loading
    const initDashboard = async () => {
      try {
        setLoading(true);
        
        // Fetch shipments and init data in parallel
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (includeClosed) params.append('include_closed', 'true');
        params.append('page', '1');
        params.append('limit', pageSize.toString());
        
        const [shipmentsResponse, initResponse] = await Promise.all([
          api.get(`/shipments?${params.toString()}`),
          api.get('/dashboard/init').catch(err => {
            console.warn('Init endpoint failed, falling back to individual calls:', err);
            return null;
          })
        ]);
        
        // Set shipments with pagination info
        if (shipmentsResponse.data) {
          if (shipmentsResponse.data.shipments) {
            setShipments(shipmentsResponse.data.shipments);
            setTotalShipments(shipmentsResponse.data.total || 0);
            setTotalPages(shipmentsResponse.data.pages || 1);
            setCurrentPage(shipmentsResponse.data.page || 1);
          } else if (Array.isArray(shipmentsResponse.data)) {
            // Legacy format fallback
            setShipments(shipmentsResponse.data);
            setTotalShipments(shipmentsResponse.data.length);
            setTotalPages(1);
          }
        }
        
        // If init succeeded, use combined data
        if (initResponse?.data) {
          const initData = initResponse.data;
          if (initData.stats) setStats(initData.stats);
          if (initData.master_data) {
            setMasterData({
              ssls: initData.master_data.ssls || [],
              terminals: initData.master_data.terminals || [],
              drivers: initData.master_data.drivers || [],
              yards: initData.master_data.yards || [],
              chassis_types: initData.master_data.chassis_types || [],
              locations: initData.master_data.locations || [],
              container_types: initData.master_data.container_types || [],
              customs_statuses: initData.master_data.customs_statuses || [],
              driver_users: initData.driver_users || []
            });
          }
          if (initData.customers) setCustomers(initData.customers);
          // Apply user preferences (status colors and order) from init data
          if (initData.preferences) {
            if (initData.preferences.status_colors) {
              setStatusColors(prev => ({ ...prev, ...initData.preferences.status_colors }));
            }
            if (initData.preferences.status_order && initData.preferences.status_order.length > 0) {
              // iter 264d.16 — merge with DEFAULT (see fetchUserPreferences)
              setStatusOrder(mergeStatusOrderWithDefault(initData.preferences.status_order, DEFAULT_STATUS_ORDER));
            }
            // iter 265.3 — hydrate the Sticky/Auto-clear toggle from the init
            // payload too so it survives reload. Prev bug: only the fallback
            // fetchUserPreferences path hydrated it, which never runs on the
            // successful init path.
            if (typeof initData.preferences.filter_clear_selection === 'boolean') {
              setFilterClearSelection(initData.preferences.filter_clear_selection);
            }
          }
          if (initData.column_preferences && initData.column_preferences.length > 0) {
            // Preserve saved column order and visibility
            const savedColumns = initData.column_preferences;
            setColumns(prev => {
              // Merge saved columns with defaults, preserving saved order
              const orderedColumns = [];
              
              // First, add columns in the saved order
              savedColumns.forEach(savedCol => {
                const defaultCol = prev.find(d => d.id === savedCol.id);
                if (defaultCol) {
                  orderedColumns.push({ ...defaultCol, ...savedCol });
                }
              });
              
              // Add any new columns that weren't in saved preferences
              prev.forEach(col => {
                if (!orderedColumns.find(c => c.id === col.id)) {
                  if (col.id === 'marker') {
                    orderedColumns.unshift(col);
                  } else {
                    orderedColumns.push(col);
                  }
                }
              });
              
              return orderedColumns;
            });
          }
          if (initData.company) setCompanyData(initData.company);
          else if (user?.role === 'admin') setCompanySetupOpen(true);
        } else {
          // Fallback to individual calls if init failed
          fetchStats();
          fetchMasterData();
          fetchCustomers();
          fetchUserPreferences();
          loadColumnPreferences();
          if (user?.role === 'admin') {
            try {
              const companyRes = await api.get('/company');
              if (companyRes.data) setCompanyData(companyRes.data);
              else setCompanySetupOpen(true);
            } catch { setCompanySetupOpen(true); }
          }
        }
        
      } catch (error) {
        console.error('Dashboard init error:', error);
        // Don't show error toast if data is loading - fallback will handle it
      } finally {
        setLoading(false);
      }
    };
    
    initDashboard();
  }, [api, user?.role]);

  const handleCompanySetupComplete = () => {
    setCompanySetupOpen(false);
    // Refresh data after setup
    fetchMasterData();
    toast.success('Company setup complete!');
  };

  const handleDispatch = (shipment) => {
    setSelectedShipment(shipment);
    setDispatchModalOpen(true);
  };

  const handleEdit = (shipment) => {
    setSelectedShipment(shipment);
    setEditSheetOpen(true);
  };

  // iter 294.5 — Track Now: fire an immediate re-check of the terminal
  // (eModal / WBCT / APM / LBCT / …) for a single shipment without opening
  // Edit → Terminal Availability. Uses the same async-check endpoint the
  // Edit dialog uses so the polling / toast semantics are consistent.
  const [trackingBusyShipmentId, setTrackingBusyShipmentId] = React.useState(null);
  const trackingPollRef = React.useRef({});
  const handleTrackNow = async (shipment) => {
    if (!shipment?.id) return;
    if (!shipment.terminal_id) {
      toast.error('Assign a terminal to this shipment first');
      return;
    }
    setTrackingBusyShipmentId(shipment.id);
    try {
      const { data: task } = await api.post(
        `/terminal-integration/async-check/${shipment.id}`,
        {},
      );
      const taskId = task?.task_id;
      if (!taskId) {
        toast.error(task?.error || 'Could not start terminal check');
        setTrackingBusyShipmentId(null);
        return;
      }
      toast.info(`Checking terminal for ${shipment.container_number || shipment.id}…`, { duration: 4000 });
      // Poll the task; give up after ~90 seconds.
      let elapsed = 0;
      const interval = setInterval(async () => {
        elapsed += 3;
        try {
          const { data: t } = await api.get(`/terminal-integration/async-task/${taskId}`);
          if (t.status === 'completed') {
            clearInterval(interval);
            setTrackingBusyShipmentId(null);
            const d = t.result || {};
            const lfd = d.lfd ? ` · LFD ${d.lfd}` : '';
            const avail = d.availability ? ` · ${d.availability}` : '';
            const holds = Array.isArray(d.holds) && d.holds.length ? ` · ${d.holds.length} hold(s)` : '';
            toast.success(`Updated ${shipment.container_number || shipment.id}${avail}${lfd}${holds}`, { duration: 8000 });
            fetchShipments();
          } else if (t.status === 'failed') {
            clearInterval(interval);
            setTrackingBusyShipmentId(null);
            toast.error(t.error || 'Terminal check failed', {
              duration: 12000,
              description: t.error_reason || undefined,
            });
          } else if (elapsed >= 90) {
            clearInterval(interval);
            setTrackingBusyShipmentId(null);
            toast.warning('Terminal check is still running in the background — refresh in a minute to see the result');
          }
        } catch (pollErr) {
          // Transient error while polling — keep going.
          if (elapsed >= 90) {
            clearInterval(interval);
            setTrackingBusyShipmentId(null);
          }
        }
      }, 3000);
      trackingPollRef.current[shipment.id] = interval;
    } catch (e) {
      console.error('track-now error', e);
      setTrackingBusyShipmentId(null);
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.message ||
        'Could not start terminal check';
      toast.error(detail, { duration: 12000 });
    }
  };

  const handleLoadCreated = (newShipmentId) => {
    setAddSheetOpen(false);
    if (newShipmentId) {
      setLastEditedShipmentId(newShipmentId);
      // Broadcast to other tabs
      broadcast(SYNC_TYPES.SHIPMENT_CREATED, { shipmentId: newShipmentId });
    }
    fetchShipments();
    fetchStats();
    toast.success('Load created successfully');
  };

  const handleLoadUpdated = () => {
    // Keep track of the shipment that was just edited
    if (selectedShipment) {
      setLastEditedShipmentId(selectedShipment.id);
      // Broadcast to other tabs
      broadcast(SYNC_TYPES.REFRESH_ALL, { shipmentId: selectedShipment.id });
    }
    setEditSheetOpen(false);
    setSelectedShipment(null);
    fetchShipments();
    toast.success('Load updated successfully');
  };

  // Handle location/status change from LocationChangeModal
  const handleLocationStatusChange = () => {
    // Set the green marker on the shipment that was just updated
    if (locationShipment) {
      setLastEditedShipmentId(locationShipment.id);
      // Broadcast to other tabs
      broadcast(SYNC_TYPES.REFRESH_ALL, { shipmentId: locationShipment.id });
    }
    setLocationModalOpen(false);
    setLocationShipment(null);
    fetchShipments();
    toast.success('Location/status updated successfully');
  };

  // Handle delete load
  const handleDeleteLoad = async () => {
    if (!deleteShipment || !deletePin) {
      toast.error('Please enter the deletion PIN');
      return;
    }
    
    setDeletingLoad(true);
    try {
      // First verify the PIN
      await api.post('/company/verify-deletion-pin', { pin: deletePin });
      
      // Then delete the shipment
      await api.delete(`/shipments/${deleteShipment.id}`);
      
      // Broadcast deletion to other tabs
      broadcast(SYNC_TYPES.SHIPMENT_DELETED, {
        shipmentId: deleteShipment.id
      });
      
      toast.success('Load deleted successfully');
      setDeleteModalOpen(false);
      setDeleteShipment(null);
      setDeletePin('');
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete load');
    } finally {
      setDeletingLoad(false);
    }
  };

  // Handle inline date save (for CellDateColorPicker)
  // DATE_FIELDS lives in shipmentDashboardConstants — imported above.
  const handleInlineDateSave = async (shipmentId, field, value) => {
    try {
      let valueToSend = value || null;
      
      // Only add T12:00:00 suffix for actual date fields, not for IDs or other values
      if (valueToSend && DATE_FIELDS.includes(field)) {
        // dateValue is in YYYY-MM-DD format from the date input
        // Add T12:00:00 to avoid timezone issues (noon ensures same day in any timezone)
        valueToSend = `${value}T12:00:00`;
      }
      
      await api.put(`/shipments/${shipmentId}`, {
        [field]: valueToSend
      });
      
      // Update local state
      setShipments(prev => prev.map(s => 
        s.id === shipmentId 
          ? { ...s, [field]: valueToSend }
          : s
      ));
      
      // Set green marker on the edited shipment
      setLastEditedShipmentId(shipmentId);
      
      // Broadcast to other tabs
      broadcast(SYNC_TYPES.SHIPMENT_UPDATED, {
        shipmentId,
        updates: { [field]: valueToSend }
      });
      
      // Show appropriate success message based on field type
      const message = DATE_FIELDS.includes(field) ? 'Date updated successfully' : 'Updated successfully';
      toast.success(message);
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Generic factory for time-window save handlers (appointment / delivery / pickup / mty_return).
  // All 4 fields persist the same way: PUT {prefix_time_from, prefix_time_to} → mirror local
  // state, mark last-edited, broadcast cross-tab. The factory keeps the wiring DRY.
  const makeTimeWindowSaver = (prefix, label) => async (shipmentId, timeFrom, timeTo) => {
    const fromKey = `${prefix}_time_from`;
    const toKey = `${prefix}_time_to`;
    const payload = { [fromKey]: timeFrom || null, [toKey]: timeTo || null };
    try {
      await api.put(`/shipments/${shipmentId}`, payload);

      setShipments((prev) =>
        prev.map((s) =>
          s.id === shipmentId ? { ...s, [fromKey]: timeFrom, [toKey]: timeTo } : s
        )
      );

      setLastEditedShipmentId(shipmentId);

      broadcast(SYNC_TYPES.SHIPMENT_UPDATED, {
        shipmentId,
        updates: { [fromKey]: timeFrom, [toKey]: timeTo },
      });

      toast.success(`${label} time updated`);
    } catch (error) {
      toast.error(`Failed to update ${label.toLowerCase()} time`);
    }
  };

  const handleAppointmentTimeSave = makeTimeWindowSaver('appointment', 'Appointment');
  const handleDeliveryTimeSave = makeTimeWindowSaver('delivery', 'Delivery');
  const handlePickupTimeSave = makeTimeWindowSaver('pickup', 'Pickup');
  const handleMtyReturnTimeSave = makeTimeWindowSaver('mty_return', 'MTY Return');

  // Handle chassis save (type + number)
  const handleChassisSave = async (shipmentId, chassisType, chassisNumber) => {
    try {
      await api.put(`/shipments/${shipmentId}`, {
        chassis_type: chassisType || null,
        chassis_number: chassisNumber || null
      });
      
      // Update local state
      setShipments(prev => prev.map(s => 
        s.id === shipmentId 
          ? { ...s, chassis_type: chassisType, chassis_number: chassisNumber }
          : s
      ));
      
      // Set green marker on the edited shipment
      setLastEditedShipmentId(shipmentId);
      
      // Broadcast to other tabs
      broadcast(SYNC_TYPES.SHIPMENT_UPDATED, {
        shipmentId,
        updates: { chassis_type: chassisType, chassis_number: chassisNumber }
      });
      
      toast.success('Chassis updated');
    } catch (error) {
      toast.error('Failed to update chassis');
    }
  };

  // Render inline editable cell using the hook
  const renderEditableCell = (shipment, field, displayValue, colorKey) => {
    const editing = isInlineEditing(shipment.id, field);
    
    if (editing) {
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inlineEditInputRef}
            value={inlineEditValue}
            onChange={(e) => setInlineEditValue(e.target.value)}
            onKeyDown={handleInlineEditKeyDown}
            className="h-7 w-32 text-xs bg-slate-800 border-blue-500"
            onClick={(e) => e.stopPropagation()}
          />
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); handleInlineEditSave(); }} disabled={savingInlineEdit}>
            <Check className="h-3 w-3 text-green-400" />
          </Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); cancelInlineEdit(); }}>
            <X className="h-3 w-3 text-red-400" />
          </Button>
        </div>
      );
    }
    
    return (
      <CellColorPicker 
        containerNumber={shipment.container_number}
        currentColor={shipment.cell_colors?.[colorKey]}
        onColorChange={(color) => handleCellColorChange(shipment.id, colorKey, color)}
      >
        <ColoredText 
          color={shipment.cell_colors?.[colorKey]}
          className="text-xs cursor-pointer hover:bg-slate-700/50 px-1 py-0.5 rounded min-w-[40px] inline-block"
          onClick={() => canEdit && startInlineEdit(shipment.id, field, shipment[field])}
          title={canEdit ? "Click to edit" : "View only"}
        >
          {displayValue || '-'}
        </ColoredText>
      </CellColorPicker>
    );
  };

  const handleDispatchCreated = () => {
    // Set green marker on the dispatched shipment
    if (selectedShipment) {
      setLastEditedShipmentId(selectedShipment.id);
    }
    setDispatchModalOpen(false);
    setSelectedShipment(null);
    toast.success('Dispatch sent successfully');
  };

  // Update cell color for a specific shipment column
  const handleCellColorChange = async (shipmentId, columnKey, color) => {
    try {
      await api.put(`/shipments/${shipmentId}/cell-color`, {
        column_key: columnKey,
        color: color
      });
      
      // Update local state
      setShipments(prev => prev.map(s => {
        if (s.id === shipmentId) {
          const cell_colors = { ...(s.cell_colors || {}) };
          if (color) {
            cell_colors[columnKey] = color;
          } else {
            delete cell_colors[columnKey];
          }
          return { ...s, cell_colors };
        }
        return s;
      }));
      
      // Broadcast to other tabs
      broadcast(SYNC_TYPES.CELL_COLOR_UPDATED, {
        shipmentId,
        field: columnKey,
        color
      });
      
      toast.success('Color updated');
    } catch (error) {
      toast.error('Failed to update color');
    }
  };

  // COLOR_PICKABLE_COLUMNS lives in shipmentDashboardConstants — imported above.

  // Shipments are already filtered server-side, just sort by status order
  // Handle column header click for sorting
  const handleColumnSort = (columnId) => {
    if (sortColumn === columnId) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, start with ascending
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  // Clear sorting (return to status order)
  const clearSort = () => {
    setSortColumn(null);
    setSortDirection('asc');
  };

  // Sort shipments by selected column (or fall back to status order). Delegated
  // to the extracted comparator so the dashboard stays focused on UI.
  const sortedShipments = sortShipments(shipments, sortColumn, sortDirection, statusOrder);

  // Copy container numbers to clipboard (selected or all visible)
  const copyContainerNumbers = () => {
    let containers;
    if (selectedCount > 0) {
      // Copy only selected containers
      containers = shipments
        .filter(s => isSelected(s.id))
        .map(s => s.container_number)
        .join('\n');
      toast.success(`Copied ${selectedCount} selected container numbers`);
    } else {
      // Copy all visible containers
      containers = shipments.map(s => s.container_number).join('\n');
      toast.success(`Copied ${shipments.length} container numbers`);
    }
    navigator.clipboard.writeText(containers).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  // Bulk status update
  // Bulk operations live in the dedicated hook so the dashboard stays focused
  // on UI wiring. The hook owns `bulkUpdating` / `bulkAvailabilityChecking`
  // and exposes one handler per operation.
  const {
    bulkUpdating,
    bulkAvailabilityChecking,
    handleBulkStatusUpdate,
    handleBulkLFDUpdate,
    handleBulkTerminalUpdate,
    handleBulkYardUpdate,
    handleBulkAvailabilityCheck,
    handleBulkAutoStatus,
  } = useShipmentBulkActions({
    api,
    selectedShipments,
    selectedCount,
    deselectAll,
    broadcast,
    fetchShipments,
    masterData,
    closeStatusModal: () => { setBulkStatusModalOpen(false); setBulkStatus(''); },
    closeLFDModal: () => { setBulkLFDModalOpen(false); setBulkLFD(null); },
    closeTerminalModal: () => { setBulkTerminalModalOpen(false); setBulkTerminal(''); },
    closeYardModal: () => { setBulkYardModalOpen(false); setBulkYard(''); },
  });

  // Saved searches — let dispatchers stash filter combos for one-click recall.
  // The hook lazy-loads on mount; the popover lives in ShipmentFilterBar.
  const {
    items: savedSearches,
    loading: savedSearchesLoading,
    save: saveSearch,
    remove: removeSavedSearch,
  } = useSavedSearches({ api, dashboard: 'imports' });

  // Capture the current filter state as a serializable snapshot — exactly
  // the shape `applySavedSearch` consumes below, so save/reapply round-trips.
  const buildFilterSnapshot = () => ({
    searchTerm,
    statusFilter,
    sslFilter,
    driverFilter,
    customerFilter,
    includeClosed,
    hiddenStatuses,
  });

  const applySavedSearch = (search) => {
    const f = search?.filters || {};
    setSearchTerm(f.searchTerm || '');
    setStatusFilter(f.statusFilter || '');
    setSslFilter(f.sslFilter || '');
    setDriverFilter(f.driverFilter || '');
    setCustomerFilter(f.customerFilter || '');
    setIncludeClosed(!!f.includeClosed);
    setHiddenStatuses(Array.isArray(f.hiddenStatuses) ? f.hiddenStatuses : []);
  };

  const handleSaveCurrentSearch = async (name) => {
    const result = await saveSearch(name, buildFilterSnapshot());
    return !!result;
  };

  // Bulk auto-update status based on existing LFD data

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter('');
    setSslFilter('');
    setDriverFilter('');
    setCustomerFilter('');
    setSearchTerm('');
  };

  // iter 265.2 — When the user clicks the Drayly logo, reset filters AND
  // deselect any bulk-selected containers so the "home" click always yields
  // the default view. In production users were "stuck in filters" with no
  // way back except a hard refresh.
  useLogoReset(() => {
    clearFilters();
    setIncludeClosed(false);
    setHiddenStatuses([]);
    try { deselectAll?.(); } catch (_) { /* ignore */ }
  });

  // iter 265.3 — Auto-clear bulk selection when a filter changes (opt-in).
  useClearSelectionOnFilterChange({
    enabled: filterClearSelection,
    deselectAll,
    deps: [statusFilter, sslFilter, driverFilter, customerFilter, searchTerm, includeClosed, JSON.stringify(hiddenStatuses)],
  });

  const hasActiveFilters = statusFilter || sslFilter || driverFilter || customerFilter || searchTerm;

  // getDaysUntilLFD + shouldLFDBlink — imported from shipmentDashboardHelpers.

  // Save status order to user preferences
  const saveStatusOrder = async (newOrder) => {
    try {
      await api.put('/user/preferences', { status_order: newOrder });
      setStatusOrder(newOrder);
      setStatusOrderModalOpen(false);
      toast.success('Status order saved');
    } catch (error) {
      toast.error('Failed to save status order');
    }
  };

  // Column label translation helper — builds a closure bound to `t` once per
  // render so the table can call it without re-resolving translations.
  const getColumnLabel = buildColumnLabelResolver(t);

  const getMasterName = (collection, id) => {
    const item = masterData[collection]?.find(i => i.id === id);
    return item?.name || item?.code || '-';
  };

  // Get code/abbreviation from master data (for SSL SCAC and Terminal abbreviations)
  const getMasterCode = (collection, id) => {
    const item = masterData[collection]?.find(i => i.id === id);
    // For SSLs: prefer scac_code, then code, then name
    // For Terminals: prefer code/abbreviation, then name
    return item?.scac_code || item?.code || item?.abbreviation || item?.name || '-';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Handle invalid string values
    if (typeof dateStr === 'string') {
      const trimmed = dateStr.trim();
      if (!trimmed || trimmed.toLowerCase() === 'none' || trimmed.toLowerCase() === 'null') {
        return '-';
      }
    }
    // Use the company timezone settings to format the date
    const formatted = formatDateWithSettings(dateStr);
    return formatted || '-';
  };

  // formatTime / getFlexColumnWidth / getMinColumnWidth — extracted helpers.
  // Bind the visible-columns argument here so callsites stay terse.
  const formatTime = formatTimeWindow;
  const getFlexColumnWidth = (col) => resolveFlexColumnWidth(col, visibleColumns);
  const getMinColumnWidth = resolveMinColumnWidth;

  // Render cell content - now delegates to ShipmentTableCell component
  const renderCell = (columnId, shipment) => (
    <ShipmentTableCell
      columnId={columnId}
      shipment={shipment}
      lastEditedShipmentId={lastEditedShipmentId}
      isCustomer={isCustomer}
      canEdit={canEdit}
      isDark={isDark}
      onDispatch={handleDispatch}
      onEdit={handleEdit}
      onLocationChange={(s) => {
        setLocationShipment(s);
        setLocationModalOpen(true);
      }}
      onTrackNow={handleTrackNow}
      trackingBusy={trackingBusyShipmentId}
      onCellColorChange={handleCellColorChange}
      onInlineDateSave={handleInlineDateSave}
      onAppointmentTimeSave={handleAppointmentTimeSave}
      onDeliveryTimeSave={handleDeliveryTimeSave}
      onPickupTimeSave={handlePickupTimeSave}
      onMtyReturnTimeSave={handleMtyReturnTimeSave}
      onCommentsClick={handleCommentsClick}
      onTimelineClick={handleTimelineClick}
      onChassisSave={handleChassisSave}
      inlineEditCell={inlineEditCell}
      inlineEditValue={inlineEditValue}
      setInlineEditValue={setInlineEditValue}
      startInlineEdit={startInlineEdit}
      handleInlineEditSave={handleInlineEditSave}
      cancelInlineEdit={cancelInlineEdit}
      masterData={masterData}
      getMasterName={getMasterName}
      getMasterCode={getMasterCode}
      formatDate={formatDate}
      formatTime={formatTime}
      shouldLFDBlink={shouldLFDBlink}
      getDaysUntilLFD={getDaysUntilLFD}
      statusColors={STATUS_COLORS}
      t={t}
    />
  );

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="font-heading font-bold text-xl sm:text-2xl text-white">{t('shipments.title')}</h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-1">
            {stats ? `${stats.active_shipments} ${t('dashboard.activeDispatches').toLowerCase()} / ${stats.total_shipments} ${t('common.total').toLowerCase()}` : t('dashboard.loading')}
            {hasActiveFilters && ` • Showing ${shipments.length} filtered results`}
            {totalShipments > 0 && ` (${totalShipments} total)`}
          </p>
        </div>
        {/* Action Buttons - Scrollable on mobile */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={copyContainerNumbers}
            className="border-slate-700 whitespace-nowrap flex-shrink-0"
            data-testid="copy-containers-btn"
            title={t('tooltips.copyContainerNumbers')}
          >
            <Copy className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('dashboard.copyContainers', 'Copy #s')}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setColumnSettingsOpen(true)}
            className="border-slate-700 whitespace-nowrap flex-shrink-0"
            data-testid="column-settings-btn"
          >
            <Settings2 className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('dashboard.columnSettings')}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatusOrderModalOpen(true)}
            className="border-slate-700 whitespace-nowrap flex-shrink-0"
            data-testid="status-order-btn"
          >
            <ListOrdered className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('dashboard.statusOrder')}</span>
          </Button>
          {canEdit && (
            <>
              <Button
                onClick={() => setBulkDOUploadOpen(true)}
                variant="outline"
                size="sm"
                className="border-purple-500/50 hover:border-purple-400 hover:bg-purple-500/10 whitespace-nowrap flex-shrink-0"
                data-testid="bulk-upload-btn"
              >
                <Upload className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('dashboard.bulkUpload')}</span>
              </Button>
              <Button
                onClick={() => setAddSheetOpen(true)}
                size="sm"
                className="bg-blue-600 hover:bg-blue-500 whitespace-nowrap flex-shrink-0"
                data-testid="add-load-btn"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('dashboard.addLoad')}</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Customer Welcome Banner */}
      {isCustomer && (
        <Card className="bg-blue-900/30 border-blue-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
              <Truck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-medium">Welcome, {user?.name || 'Customer'}</h3>
              <p className="text-blue-300 text-sm">Viewing your shipments in real-time. Contact us if you need assistance.</p>
            </div>
          </div>
        </Card>
      )}

      {/* Tracking health banner — surfaces credential gaps when >50% of recent runs fail */}
      <TrackingHealthBanner api={api} />

      {/* Filters - Using extracted component */}
      <ShipmentFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t('dashboard.searchPlaceholder')}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        sslFilter={sslFilter}
        onSslFilterChange={setSslFilter}
        driverFilter={driverFilter}
        onDriverFilterChange={setDriverFilter}
        customerFilter={customerFilter}
        onCustomerFilterChange={setCustomerFilter}
        statusOptions={Object.keys(STATUS_COLORS)}
        sslOptions={masterData.ssls}
        driverOptions={masterData.drivers}
        customerOptions={customers}
        includeClosed={includeClosed}
        onIncludeClosedChange={setIncludeClosed}
        hiddenStatuses={hiddenStatuses}
        onHiddenStatusesChange={setHiddenStatuses}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClearSort={clearSort}
        columnsChanged={columnsChanged}
        savingColumns={savingColumns}
        onSaveColumns={() => saveColumnPreferences(columns)}
        onRefresh={() => fetchShipments()}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        wsConnected={wsConnected}
        savedSearches={savedSearches}
        savedSearchesLoading={savedSearchesLoading}
        onApplySavedSearch={applySavedSearch}
        onSaveCurrentSearch={handleSaveCurrentSearch}
        onDeleteSavedSearch={removeSavedSearch}
        t={t}
      />

      {/* Bulk Selection Bar - not for customers */}
      <BulkSelectionBar
        selectedCount={selectedCount}
        show={canEdit}
        onClearSelection={deselectAll}
        filterClearSelection={filterClearSelection}
        onToggleFilterClear={toggleFilterClearSelection}
        actions={[
          { label: 'Change Status', onClick: () => setBulkStatusModalOpen(true), className: 'border-blue-600 text-blue-300 hover:bg-blue-600', testId: 'bulk-status-btn' },
          { label: 'Change LFD', onClick: () => setBulkLFDModalOpen(true), className: 'border-green-600 text-green-300 hover:bg-green-600', testId: 'bulk-lfd-btn' },
          { label: 'Change Yard', onClick: () => setBulkYardModalOpen(true), className: 'border-purple-600 text-purple-300 hover:bg-purple-600', testId: 'bulk-yard-btn' },
          { label: 'Set Terminal', onClick: () => setBulkTerminalModalOpen(true), className: 'border-sky-600 text-sky-300 hover:bg-sky-600', testId: 'bulk-terminal-btn' },
          { 
            label: bulkAvailabilityChecking ? 'Checking...' : 'Check Availability', 
            onClick: handleBulkAvailabilityCheck, 
            className: 'border-amber-600 text-amber-300 hover:bg-amber-600', 
            testId: 'bulk-availability-btn',
            disabled: bulkAvailabilityChecking
          },
          { 
            label: bulkUpdating ? 'Updating...' : 'Auto Status (LFD)', 
            onClick: handleBulkAutoStatus, 
            className: 'border-cyan-600 text-cyan-300 hover:bg-cyan-600', 
            testId: 'bulk-auto-status-btn',
            disabled: bulkUpdating
          }
        ]}
      />

      {/* Data Table - Using shared DataTable component */}
      <DataTable
        data={sortedShipments}
        columns={visibleColumns}
        loading={loading}
        emptyMessage={hasActiveFilters ? t('dashboard.noShipmentsFilter') : t('dashboard.noShipmentsCreate')}
        idField="id"
        // Sorting
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onColumnSort={handleColumnSort}
        // Drag & Drop
        draggedColumn={draggedColumn}
        dragOverColumn={dragOverColumn}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        // Resize
        onResizeStart={handleResizeStart}
        // Selection
        showSelection={true}
        isAllSelected={isAllSelected}
        isSomeSelected={isSomeSelected}
        onToggleSelectAll={toggleSelectAll}
        isSelected={isSelected}
        onToggleSelect={toggleSelectShipment}
        // Styling
        getRowStyle={(shipment) => ({ 
          backgroundColor: getRowBackgroundColor(shipment.status),
          color: getTextColorForStatus(shipment.status)
        })}
        getCellStyle={(col) => ({
          minWidth: getMinColumnWidth(col.id),
          overflow: col.id === 'remarks' || col.id === 'deliver_to' ? 'visible' : 'hidden',
          textOverflow: col.id === 'remarks' || col.id === 'deliver_to' ? 'clip' : 'ellipsis',
          whiteSpace: col.id === 'remarks' || col.id === 'deliver_to' ? 'normal' : 'nowrap',
          maxHeight: col.id === 'remarks' ? '3em' : 'auto'
        })}
        // Rendering
        renderCell={renderCell}
        // Column helpers
        getColumnWidth={(col) => getFlexColumnWidth(col)}
        getMinColumnWidth={getMinColumnWidth}
        getColumnLabel={getColumnLabel}
        // Protected columns
        protectedColumns={PROTECTED_COLUMNS}
        // Ref
        tableRef={tableRef}
        // Test ID
        testIdPrefix="shipment-row"
      />
      
      {/* Pagination removed — all loads shown on single page */}

      {/* Status Color Legend - Using extracted component */}
      <StatusColorLegend
        statuses={STATUS_OPTIONS}
        statusColors={statusColors}
        defaultColors={DEFAULT_STATUS_ROW_COLORS}
        activeFilter={statusFilter}
        onFilterChange={setStatusFilter}
        onCustomizeClick={() => setColorSettingsOpen(true)}
        showCustomize={user?.role === 'admin'}
      />

      {/* Modals & Sheets */}
      <AddLoadSheet
        open={addSheetOpen}
        onOpenChange={setAddSheetOpen}
        masterData={masterData}
        customers={customers}
        onSuccess={handleLoadCreated}
        onCustomersChange={fetchCustomers}
        fullPage={false}
      />
      
      <EditLoadSheet
        open={editSheetOpen}
        onOpenChange={(open) => {
          setEditSheetOpen(open);
          if (!open) {
            setSelectedShipment(null);
            // Refresh dashboard data so any inline saves made INSIDE the sheet
            // (status changes, document uploads, etc.) reflect immediately on
            // the table without waiting for the next polling tick.
            fetchShipments();
          }
        }}
        shipment={selectedShipment}
        masterData={masterData}
        customers={customers}
        onSuccess={handleLoadUpdated}
      />
      
      <DispatchModal
        open={dispatchModalOpen}
        onOpenChange={(open) => {
          setDispatchModalOpen(open);
          if (!open) setSelectedShipment(null);
        }}
        shipment={selectedShipment}
        drivers={masterData.drivers}
        terminals={masterData.terminals}
        yards={masterData.yards}
        customers={customers}
        onSuccess={handleDispatchCreated}
      />

      <ColumnSettingsModal
        open={columnSettingsOpen}
        onOpenChange={setColumnSettingsOpen}
        columns={columns}
        onSave={saveColumnPreferences}
        loading={savingColumns}
      />

      <LocationChangeModal
        open={locationModalOpen}
        onOpenChange={setLocationModalOpen}
        shipment={locationShipment}
        terminals={masterData.terminals}
        yards={masterData.yards}
        drivers={masterData.drivers}
        onUpdate={handleLocationStatusChange}
        api={api}
        key={`loc-modal-${masterData.drivers.length}`}
      />

      <StatusColorSettings
        open={colorSettingsOpen}
        onOpenChange={setColorSettingsOpen}
        currentColors={statusColors}
        onSave={(newColors) => {
          setStatusColors(newColors);
        }}
        api={api}
      />

      {/* Bulk Status Update Modal - Using extracted component */}
      <BulkUpdateModal
        open={bulkStatusModalOpen}
        onClose={() => {
          setBulkStatusModalOpen(false);
          setBulkStatus('');
        }}
        title="Bulk Status Update"
        description={`Change status for ${selectedCount} selected shipments`}
        selectedCount={selectedCount}
        inputType="select"
        value={bulkStatus}
        onValueChange={setBulkStatus}
        options={Object.keys(STATUS_COLORS)}
        onSubmit={() => handleBulkStatusUpdate(bulkStatus)}
        loading={bulkUpdating}
        submitText="Update"
        submitClassName="bg-blue-600 hover:bg-blue-500"
        testIdPrefix="bulk-status"
      />

      {/* Bulk LFD Update Modal - Using extracted component */}
      <BulkUpdateModal
        open={bulkLFDModalOpen}
        onClose={() => {
          setBulkLFDModalOpen(false);
          setBulkLFD(null);
        }}
        title="Bulk LFD Update"
        description={`Change LFD (Last Free Date) for ${selectedCount} selected shipments`}
        selectedCount={selectedCount}
        inputType="date"
        value={bulkLFD}
        onValueChange={setBulkLFD}
        onSubmit={() => handleBulkLFDUpdate(bulkLFD)}
        loading={bulkUpdating}
        submitText="Update"
        submitClassName="bg-green-600 hover:bg-green-500"
        testIdPrefix="bulk-lfd"
      />

      {/* Bulk Yard Update Modal - Using extracted component */}
      <BulkUpdateModal
        open={bulkYardModalOpen}
        onClose={() => {
          setBulkYardModalOpen(false);
          setBulkYard('');
        }}
        title="Bulk Yard Update"
        description={`Change Yard for ${selectedCount} selected shipments`}
        selectedCount={selectedCount}
        inputType="select"
        value={bulkYard}
        onValueChange={setBulkYard}
        options={masterData.yards || []}
        onSubmit={() => handleBulkYardUpdate(bulkYard)}
        loading={bulkUpdating}
        submitText="Update"
        submitClassName="bg-purple-600 hover:bg-purple-500"
        testIdPrefix="bulk-yard"
      />

      {/* Bulk Terminal Assign Modal — fixes "untracked" containers in batch */}
      <BulkUpdateModal
        open={bulkTerminalModalOpen}
        onClose={() => {
          setBulkTerminalModalOpen(false);
          setBulkTerminal('');
        }}
        title="Set Terminal"
        description={`Assign a terminal to ${selectedCount} selected shipments — only shipments with a terminal can be tracked by the availability scheduler`}
        selectedCount={selectedCount}
        inputType="select"
        value={bulkTerminal}
        onValueChange={setBulkTerminal}
        options={masterData.terminals || []}
        onSubmit={() => handleBulkTerminalUpdate(bulkTerminal)}
        loading={bulkUpdating}
        submitText="Assign"
        submitClassName="bg-sky-600 hover:bg-sky-500"
        testIdPrefix="bulk-terminal"
      />

      {/* Bulk DO Upload Modal */}
      <BulkDOUpload
        open={bulkDOUploadOpen}
        onOpenChange={setBulkDOUploadOpen}
        masterData={masterData}
        customers={customers}
        onSuccess={() => {
          fetchShipments();
          fetchStats();
        }}
      />
      
      {/* Company Setup Modal - shown for admin on first login */}
      <CompanySetupModal
        isOpen={companySetupOpen}
        onComplete={handleCompanySetupComplete}
        onClose={() => setCompanySetupOpen(false)}
      />
      
      {/* Status Order Modal */}
      <StatusOrderModal
        open={statusOrderModalOpen}
        onOpenChange={setStatusOrderModalOpen}
        statusOrder={statusOrder}
        onSave={saveStatusOrder}
      />

      {/* Delete Load Confirmation Modal - Using extracted component */}
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onOpenChange={(open) => {
          setDeleteModalOpen(open);
          if (!open) {
            setDeletePin('');
            setDeleteShipment(null);
          }
        }}
        title="Delete Load"
        description="This action cannot be undone. Enter the company deletion PIN to confirm."
        item={deleteShipment}
        displayFields={[
          { label: 'Container', key: 'container_number', mono: true },
          { label: 'Job #', key: 'job_number' }
        ]}
        requirePin={true}
        pin={deletePin}
        onPinChange={setDeletePin}
        onConfirm={handleDeleteLoad}
        loading={deletingLoad}
        confirmText="Delete Load"
      />

      {/* Container Comments (right-click / long-press on container number) */}
      <ContainerCommentsDialog
        open={commentsDialogOpen}
        onOpenChange={setCommentsDialogOpen}
        shipment={commentsShipment}
        api={api}
        currentUser={user}
      />

      {/* Container Timeline (right-click → Timeline option) */}
      <ContainerTimelineDialog
        open={timelineDialogOpen}
        onOpenChange={setTimelineDialogOpen}
        shipment={timelineShipment}
        api={api}
      />
    </div>
  );
};

export default ShipmentDashboard;
